import type { Residency } from '@salonomy/types';
import { Card, Pill } from '@salonomy/ui';
import { useResidencies } from '../../data/practitioner.js';
import { ROLE_LABEL } from '../../lib/rbac.js';
import { scheduleBlockLabels } from './schedule.js';

/**
 * WS11 — the practitioner-facing "My residencies" read view (brief 11 §2/§3). Lists EVERY place the
 * signed-in human works — each residency and their own private practice — tagged by salon, with the role
 * they hold there, their weekly schedule, and the sealed/lifecycle stance. This is the caller's OWN
 * memberships from useResidencies (resolved from the session, never another human's), the identity-layer
 * union the unified diary will later sit on top of.
 *
 * Read-only and self-fetching, so any signed-in user can see where they work. Every value shown is the
 * server's tagged row as-is — there is no cross-tenant join or recomputation on the client (ADR-0012 §2).
 * This is a real, usable surface (not the design-gated flagship diary); visual gaps flagged for design.
 */
export function MyResidenciesPanel() {
  const { data: residencies = [], isLoading } = useResidencies();

  // Active first, then private practice, then by join date — your live places read at the top.
  const sorted = [...residencies].sort(
    (a, b) =>
      Number(b.active) - Number(a.active) ||
      Number(b.isPrivatePractice) - Number(a.isPrivatePractice) ||
      a.salonName.localeCompare(b.salonName),
  );

  return (
    <Card>
      <div className="section-title">Where you work</div>
      <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
        One login across every salon you work in, plus your own studio. This is the same identity
        that powers your unified week.
      </div>

      {isLoading ? (
        <div className="muted" style={{ fontSize: 13, padding: '12px 0 0' }}>
          Loading your residencies…
        </div>
      ) : sorted.length === 0 ? (
        <div className="muted" style={{ fontSize: 13, padding: '12px 0 0' }}>
          You don’t have any residencies yet. When a salon invites you to rent a chair, it’ll appear
          here alongside your own studio.
        </div>
      ) : (
        <div className="settings-list" style={{ marginTop: 8 }}>
          {sorted.map((r) => (
            <ResidencyRow key={`${r.salonId}:${r.staffId}`} residency={r} />
          ))}
        </div>
      )}
    </Card>
  );
}

function ResidencyRow({ residency: r }: { residency: Residency }) {
  const blocks = scheduleBlockLabels(r.schedule);
  return (
    <div className="settings-row">
      <div>
        <div style={{ fontWeight: 600 }}>
          {r.salonName}
          {r.isPrivatePractice ? (
            <Pill tone="cobalt" className="mono">
              Your studio
            </Pill>
          ) : null}
          {r.renterId ? (
            <Pill tone="soft" className="mono">
              Sealed book
            </Pill>
          ) : null}
          {!r.active ? <Pill tone="danger">Ended</Pill> : null}
        </div>
        {blocks.length ? (
          <div className="muted" style={{ fontSize: 12.5, marginTop: 2 }}>
            {blocks.join(' · ')}
          </div>
        ) : (
          <div className="muted" style={{ fontSize: 12.5, marginTop: 2 }}>
            No schedule set here yet.
          </div>
        )}
      </div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <Pill tone="neutral">{ROLE_LABEL[r.role] ?? r.role}</Pill>
      </div>
    </div>
  );
}
